import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import backend from "../../backend";
import AuthContext from "../../AuthContext";
import { esDe } from "./pulsos";

// ─────────────────────────────────────────────────────────────────────────────
// PULSO DEL TALLER (28-ago-2026)
// Espejo OPERATIVO de PulseNegocio para el rol operator (admin también lo ve):
// lo que le sirve al taller HOY, calculado 100% en el frontend desde las OTs
// que el Dashboard ya carga (GET /workOrder) — cero backend nuevo.
//
// Tarjetas (todas cliqueables):
//   1. En proceso ahora ..... OTs en fabricación (working) → /produccion
//   2. Listas para entrega .. terminadas esperando guía (ready) → /workOrders
//   3. Sin avance ≥5 días ... OTs en proceso sin ningún cambio hace 5+ días
//                             (las trancadas — el dato de oro) → /produccion
//   4. Entregas de la semana  OTs entregadas/facturadas desde el lunes → /workOrders
//
// "Sin avance" usa updatedAt de la OT: cualquier movimiento (cambio de estado,
// edición, cierre parcial, cascada de piezas vía save de la OT) lo refresca.
// Es una aproximación honesta con los datos ya disponibles — sin endpoint nuevo.
// ─────────────────────────────────────────────────────────────────────────────

const DIAS_TRANCADA = 5;

const esEnProceso = wo => ["working", "inProcess"].includes(wo.status);
const esLista     = wo => ["ready", "done"].includes(wo.status);
const esEntregada = wo => ["delivered", "facturado", "invoiced"].includes(wo.status);

// Lunes 00:00 de la semana actual (semana laboral del taller).
const lunesDeEstaSemana = () => {
  const d = new Date();
  const day = d.getDay(); // 0 = domingo
  const diff = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const PulsoTaller = ({ workOrders = [], loading = false, isMobile = false }) => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const hasRole = auth?.can?.("ver_pulso_taller") || false;

  // OC que llegaron a recepcion@ y NO están en el ERP (vigía IMAP del backend).
  // Registrarlas es pega del operator: tarjeta roja solo si hay algo pendiente.
  const [ocPend, setOcPend] = useState(0);
  useEffect(() => {
    if (!hasRole) return;
    backend.get("mailEntrante/pendientes/count")
      .then(r => setOcPend(r.data?.n || 0))
      .catch(() => setOcPend(0));
  }, [hasRole]);

  // Cálculo derivado de las OTs ya cargadas por el Dashboard (sin fetch propio).
  const m = useMemo(() => {
    const ahora = Date.now();
    const corteTrancada = ahora - DIAS_TRANCADA * 24 * 3600e3;
    const lunes = lunesDeEstaSemana().getTime();

    const enProceso = workOrders.filter(esEnProceso);
    const listas    = workOrders.filter(esLista);
    const trancadas = enProceso
      .filter(wo => wo.updatedAt && new Date(wo.updatedAt).getTime() < corteTrancada)
      .sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt)); // la más abandonada primero
    const entregasSemana = workOrders.filter(wo =>
      esEntregada(wo) && wo.updatedAt && new Date(wo.updatedAt).getTime() >= lunes
    );

    const diasSin = wo => Math.floor((ahora - new Date(wo.updatedAt).getTime()) / (24 * 3600e3));

    return { enProceso, listas, trancadas, entregasSemana, diasSin };
  }, [workOrders]);

  if (!hasRole) return null;

  // ── tarjeta operativa (mismo lenguaje visual que PulseNegocio) ─────────────
  const Kpi = ({ icon, titulo, valor, sub, color, to, urgente, title }) => (
    <div
      onClick={() => to && navigate(to)}
      className="t-02 tap-card"
      title={title || undefined}
      onMouseEnter={e => { if (!isMobile) e.currentTarget.style.borderColor = "#3a3a3a"; }}
      onMouseLeave={e => { if (!isMobile) e.currentTarget.style.borderColor = urgente ? "#5a2020" : "#2e2e2e"; }}
      style={{
        background: urgente ? "#241416" : "#242424",
        border: `1px solid ${urgente ? "#5a2020" : "#2e2e2e"}`,
        borderRadius: 12, padding: "13px 14px", cursor: "pointer",
        minWidth: 0, display: "flex", flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".06em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {icon} {titulo}
        </span>
        <span style={{ color: "#555", fontSize: 14, lineHeight: 1, flexShrink: 0 }}>›</span>
      </div>
      {loading
        ? <div style={{ height: 26, width: "60%", background: "#2e2e2e", borderRadius: 6, opacity: 0.6 }} />
        : <div style={{ fontSize: valor == null ? 14 : 24, fontWeight: 800, color: valor == null ? "#8a8a8a" : color, lineHeight: 1.1, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{valor == null ? "—  sin datos" : valor}</div>}
      {sub && <div style={{ fontSize: 10.5, color: "#8a8a8a", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>}
    </div>
  );

  // Sub-línea de las trancadas: los números de OT concretos (hasta 3) para que
  // Catalina sepa altiro cuáles ir a mirar, sin abrir nada.
  const trancadasSub = m.trancadas.length === 0
    ? "ninguna OT detenida — taller fluyendo"
    : m.trancadas.slice(0, 3).map(wo => `OT ${wo.number} (${m.diasSin(wo)}d)`).join(" · ")
      + (m.trancadas.length > 3 ? ` · +${m.trancadas.length - 3} más` : "");

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "2px 0 9px" }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: ".07em" }}>
          Pulso del taller
        </span>
        <span style={{ fontSize: 10, color: "#777" }}>
          {new Date().toLocaleDateString("es-CL", { weekday: "long", day: "numeric", month: "long" })}
        </span>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))",
        gap: 8,
      }}>
        {/* 1 · En proceso ahora */}
        <Kpi icon="🔧" titulo="En proceso ahora" to="/produccion"
          valor={m.enProceso.length} color="#3498db"
          sub={`OT${m.enProceso.length === 1 ? "" : "s"} fabricándose en el taller`}
          title="Ver el pipeline en /produccion" />

        {/* 2 · Listas para entrega */}
        <Kpi icon="📦" titulo="Listas para entrega" to="/workOrders"
          valor={m.listas.length} color="#f39c12"
          sub="terminadas esperando guía / cierre"
          title="Cerrar con guía o factura en /trabajos" />

        {/* 3 · Sin avance hace ≥5 días — el dato de oro */}
        <Kpi icon="⏸️" titulo={`Sin avance ${DIAS_TRANCADA}+ días`} to="/produccion"
          urgente={m.trancadas.length > 0}
          valor={m.trancadas.length}
          color={m.trancadas.length > 0 ? "#e74c3c" : "#2ecc71"}
          sub={trancadasSub}
          title={m.trancadas.length > 0
            ? m.trancadas.slice(0, 8).map(wo => `OT ${wo.number} — ${wo.client?.name || wo.name || "s/cliente"} (${m.diasSin(wo)} días)`).join("\n")
            : "Ninguna OT en proceso lleva 5 días sin movimiento"} />

        {/* 4 · Entregas de la semana */}
        <Kpi icon="🚚" titulo="Entregas de la semana" to="/workOrders"
          valor={m.entregasSemana.length} color="#2ecc71"
          sub={`entregada${m.entregasSemana.length === 1 ? "" : "s"} / facturada${m.entregasSemana.length === 1 ? "" : "s"} desde el lunes`}
          title="OTs que salieron del taller esta semana" />

        {/* 5 · OC llegadas a recepcion@ sin registrar — este pulso es su dueño
            (./pulsos.js): antes también salía en el Pulso del negocio y el admin
            la veía dos veces. */}
        {esDe("taller", "ocPorRegistrar") && ocPend > 0 && (
          <Kpi icon="📨" titulo="OC por registrar" to="/oc-entrantes" urgente
            valor={ocPend} color="#e74c3c"
            sub="llegaron a recepcion@ y no están en el ERP"
            title="Correos con OC detectada que no calzan con ninguna OC registrada — click para resolverlas" />
        )}
      </div>
    </div>
  );
};

export default PulsoTaller;
